import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  LineChart as RechartsLineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from 'recharts';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  Filter, 
  ArrowUpRight, 
  ArrowDownRight,
  Info,
  RefreshCw,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";

// Create mock data types
interface Stock {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  sector: string;
  volume: number;
  marketCap: string;
  peRatio: number;
  dividendYield: number;
  description: string;
}

interface SectorPerformance {
  name: string;
  value: number;
  color: string;
}

// Mock data for stocks
const mockStocks: Stock[] = [
  {
    symbol: "AAPL",
    name: "Apple Inc.",
    price: 184.32,
    change: 2.45,
    changePercent: 1.35,
    sector: "Technology",
    volume: 23456789,
    marketCap: "$2.8T",
    peRatio: 28.5,
    dividendYield: 0.5,
    description: "Apple Inc. designs, manufactures, and markets smartphones, personal computers, tablets, wearables, and accessories worldwide."
  },
  {
    symbol: "MSFT",
    name: "Microsoft Corp.",
    price: 337.18,
    change: 5.23,
    changePercent: 1.57,
    sector: "Technology",
    volume: 18765432,
    marketCap: "$2.5T",
    peRatio: 33.7,
    dividendYield: 0.8,
    description: "Microsoft Corporation develops, licenses, and supports software, services, devices, and solutions worldwide."
  },
  {
    symbol: "GOOGL",
    name: "Alphabet Inc.",
    price: 128.75,
    change: -1.32,
    changePercent: -1.02,
    sector: "Technology",
    volume: 12345678,
    marketCap: "$1.8T",
    peRatio: 24.3,
    dividendYield: 0,
    description: "Alphabet Inc. offers various products and platforms in the United States, Europe, the Middle East, Africa, the Asia-Pacific, Canada, and Latin America."
  },
  {
    symbol: "AMZN",
    name: "Amazon.com Inc.",
    price: 143.21,
    change: 0.87,
    changePercent: 0.61,
    sector: "Consumer Discretionary",
    volume: 32456789,
    marketCap: "$1.5T",
    peRatio: 56.2,
    dividendYield: 0,
    description: "Amazon.com, Inc. engages in the retail sale of consumer products and subscriptions in North America and internationally."
  },
  {
    symbol: "TSLA",
    name: "Tesla Inc.",
    price: 223.68,
    change: -4.32,
    changePercent: -1.89,
    sector: "Consumer Discretionary",
    volume: 45678912,
    marketCap: "$708B",
    peRatio: 48.7,
    dividendYield: 0,
    description: "Tesla, Inc. designs, develops, manufactures, leases, and sells electric vehicles, and energy generation and storage systems."
  },
  {
    symbol: "JPM",
    name: "JPMorgan Chase & Co.",
    price: 157.45,
    change: 1.25,
    changePercent: 0.80,
    sector: "Financials",
    volume: 8765432,
    marketCap: "$456B",
    peRatio: 12.3,
    dividendYield: 2.5,
    description: "JPMorgan Chase & Co. operates as a financial services company worldwide."
  },
  {
    symbol: "JNJ",
    name: "Johnson & Johnson",
    price: 167.89,
    change: -0.56,
    changePercent: -0.33,
    sector: "Healthcare",
    volume: 5678912,
    marketCap: "$402B",
    peRatio: 18.9,
    dividendYield: 3.1,
    description: "Johnson & Johnson researches and develops, manufactures, and sells various products in the healthcare field worldwide."
  },
  {
    symbol: "WMT",
    name: "Walmart Inc.",
    price: 64.23,
    change: 0.78,
    changePercent: 1.23,
    sector: "Consumer Staples",
    volume: 7654321,
    marketCap: "$352B",
    peRatio: 15.2,
    dividendYield: 1.4,
    description: "Walmart Inc. engages in the operation of retail, wholesale, and other units worldwide."
  },
  {
    symbol: "XOM",
    name: "Exxon Mobil Corp.",
    price: 112.34,
    change: -2.15,
    changePercent: -1.88,
    sector: "Energy",
    volume: 9876543,
    marketCap: "$456B",
    peRatio: 8.5,
    dividendYield: 3.8,
    description: "Exxon Mobil Corporation explores for and produces crude oil and natural gas in the United States and internationally."
  },
  {
    symbol: "PG",
    name: "Procter & Gamble Co.",
    price: 158.76,
    change: 1.12,
    changePercent: 0.71,
    sector: "Consumer Staples",
    volume: 4567891,
    marketCap: "$375B",
    peRatio: 26.2,
    dividendYield: 2.4,
    description: "The Procter & Gamble Company provides branded consumer packaged goods to consumers in North and Latin America, Europe, the Asia Pacific, Greater China, India, the Middle East, and Africa."
  }
];

// Mock sector performance data
const sectorPerformanceData: SectorPerformance[] = [
  { name: 'Technology', value: 18.5, color: "#10B981" },
  { name: 'Healthcare', value: 12.3, color: "#10B981" },
  { name: 'Financials', value: -3.2, color: "#EF4444" },
  { name: 'Energy', value: -5.1, color: "#EF4444" },
  { name: 'Industrials', value: 8.7, color: "#10B981" },
  { name: 'Consumer', value: 6.2, color: "#10B981" },
  { name: 'Utilities', value: 2.1, color: "#10B981" },
];

// Historical price data (for stock charts)
const generateHistoricalData = (basePrice: number, days: number = 30) => {
  const data = [];
  let price = basePrice;
  const today = new Date();
  
  for (let i = days; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const change = (Math.random() - 0.5) * basePrice * 0.03;
    price = Math.max(price + change, price * 0.7); // Prevent price from dropping too low
    
    data.push({
      date: date.toISOString().split('T')[0],
      price: parseFloat(price.toFixed(2))
    });
  }
  
  return data;
};

export const MarketView = () => {
  // State management
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSector, setSelectedSector] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"price" | "change" | "volume" | "name" | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [selectedStock, setSelectedStock] = useState<Stock | null>(null);
  const [stockHistoricalData, setStockHistoricalData] = useState<any[]>([]);
  const [portfolio, setPortfolio] = useState<{[key: string]: {shares: number, avgPrice: number}}>({});
  const [cashBalance, setCashBalance] = useState(10000); // Start with $10,000
  const [quantity, setQuantity] = useState(1);

  // Get unique sectors from stock data
  const sectors = useMemo(() => 
    Array.from(new Set(mockStocks.map(stock => stock.sector))),
    []
  );

  // Filter and sort stocks based on user selections
  const filteredStocks = useMemo(() => {
    let filtered = mockStocks;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(stock => 
        stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stock.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sector filter
    if (selectedSector) {
      filtered = filtered.filter(stock => stock.sector === selectedSector);
    }
    
    // Apply sorting
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        let comparison = 0;
        
        switch (sortBy) {
          case "price":
            comparison = a.price - b.price;
            break;
          case "change":
            comparison = a.changePercent - b.changePercent;
            break;
          case "volume":
            comparison = a.volume - b.volume;
            break;
          case "name":
            comparison = a.name.localeCompare(b.name);
            break;
        }
        
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }
    
    return filtered;
  }, [searchTerm, selectedSector, sortBy, sortDirection]);

  // Load historical data when a stock is selected
  useEffect(() => {
    if (selectedStock) {
      const historicalData = generateHistoricalData(selectedStock.price);
      setStockHistoricalData(historicalData);
    }
  }, [selectedStock]);

  // Handle stock selection
  const handleSelectStock = (stock: Stock) => {
    setSelectedStock(stock);
    console.log(`Selected stock: ${stock.symbol}`);
  };

  // Buy stock function
  const handleBuyStock = () => {
    if (!selectedStock) {
      toast({
        title: "No stock selected",
        description: "Please select a stock to purchase",
        variant: "destructive"
      });
      return;
    }
    
    const cost = selectedStock.price * quantity;
    
    if (cost > cashBalance) {
      toast({
        title: "Insufficient funds",
        description: `You need $${cost.toFixed(2)} to buy ${quantity} shares of ${selectedStock.symbol}`,
        variant: "destructive"
      });
      return;
    }
    
    // Update portfolio
    setPortfolio(prevPortfolio => {
      const currentPosition = prevPortfolio[selectedStock.symbol] || { shares: 0, avgPrice: 0 };
      const totalShares = currentPosition.shares + quantity;
      const totalCost = (currentPosition.shares * currentPosition.avgPrice) + cost;
      const newAvgPrice = totalCost / totalShares;
      
      return {
        ...prevPortfolio,
        [selectedStock.symbol]: {
          shares: totalShares,
          avgPrice: newAvgPrice
        }
      };
    });
    
    // Update cash balance
    setCashBalance(prev => prev - cost);
    
    toast({
      title: "Purchase successful",
      description: `Bought ${quantity} shares of ${selectedStock.symbol} at $${selectedStock.price.toFixed(2)} per share`,
    });
  };

  // Sell stock function
  const handleSellStock = () => {
    if (!selectedStock) {
      toast({
        title: "No stock selected",
        description: "Please select a stock to sell",
        variant: "destructive"
      });
      return;
    }
    
    const position = portfolio[selectedStock.symbol];
    
    // Check if user owns the stock
    if (!position || position.shares < quantity) {
      toast({
        title: "Insufficient shares",
        description: `You only have ${position?.shares || 0} shares of ${selectedStock.symbol} to sell`,
        variant: "destructive"
      });
      return;
    }
    
    const revenue = selectedStock.price * quantity;
    
    // Update portfolio
    setPortfolio(prevPortfolio => {
      const updatedPosition = { ...prevPortfolio[selectedStock.symbol] };
      updatedPosition.shares -= quantity;
      
      // If no shares left, remove the stock from portfolio
      if (updatedPosition.shares <= 0) {
        const { [selectedStock.symbol]: _, ...rest } = prevPortfolio;
        return rest;
      }
      
      return {
        ...prevPortfolio,
        [selectedStock.symbol]: updatedPosition
      };
    });
    
    // Update cash balance
    setCashBalance(prev => prev + revenue);
    
    toast({
      title: "Sale successful",
      description: `Sold ${quantity} shares of ${selectedStock.symbol} at $${selectedStock.price.toFixed(2)} per share`,
    });
  };

  // Toggle sort direction
  const handleSortChange = (value: "price" | "change" | "volume" | "name") => {
    if (sortBy === value) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortBy(value);
      setSortDirection("desc");
    }
  };

  // Calculate portfolio value
  const portfolioValue = useMemo(() => {
    return Object.entries(portfolio).reduce((total, [symbol, position]) => {
      const stock = mockStocks.find(s => s.symbol === symbol);
      if (stock) {
        return total + (stock.price * position.shares);
      }
      return total;
    }, 0);
  }, [portfolio]);

  // Calculate total account value
  const totalAccountValue = cashBalance + portfolioValue;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Market Overview</CardTitle>
          <CardDescription>Research and trade stocks with virtual money</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Account Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-gray-50 dark:bg-gray-900">
              <CardContent className="p-4">
                <h3 className="text-sm text-gray-500">Cash Balance</h3>
                <p className="text-xl font-bold">${cashBalance.toFixed(2)}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-50 dark:bg-gray-900">
              <CardContent className="p-4">
                <h3 className="text-sm text-gray-500">Portfolio Value</h3>
                <p className="text-xl font-bold">${portfolioValue.toFixed(2)}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-50 dark:bg-gray-900">
              <CardContent className="p-4">
                <h3 className="text-sm text-gray-500">Total Account Value</h3>
                <p className="text-xl font-bold">${totalAccountValue.toFixed(2)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
                <Input
                  placeholder="Search stocks by name or symbol..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex flex-row gap-2">
              <Select value={selectedSector || undefined} onValueChange={(value) => setSelectedSector(value || null)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by sector" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sectors</SelectItem>
                  {sectors.map((sector) => (
                    <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={sortBy || undefined} onValueChange={(value: any) => handleSortChange(value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="price">Price</SelectItem>
                  <SelectItem value="change">% Change</SelectItem>
                  <SelectItem value="volume">Volume</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="icon" onClick={() => setSortDirection(prev => prev === "asc" ? "desc" : "asc")}>
                {sortDirection === "asc" ? "↑" : "↓"}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Stock List */}
            <div className="lg:col-span-2">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-4 text-sm font-medium text-gray-500">Symbol</th>
                      <th className="text-left py-2 px-4 text-sm font-medium text-gray-500">Name</th>
                      <th className="text-right py-2 px-4 text-sm font-medium text-gray-500">Price</th>
                      <th className="text-right py-2 px-4 text-sm font-medium text-gray-500">Change</th>
                      <th className="text-center py-2 px-4 text-sm font-medium text-gray-500">Sector</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStocks.length > 0 ? (
                      filteredStocks.map((stock) => (
                        <tr 
                          key={stock.symbol} 
                          className={`border-b hover:bg-gray-50 cursor-pointer transition-colors ${selectedStock?.symbol === stock.symbol ? 'bg-primary/10' : ''}`}
                          onClick={() => handleSelectStock(stock)}
                        >
                          <td className="py-3 px-4">
                            <span className="font-medium">{stock.symbol}</span>
                          </td>
                          <td className="py-3 px-4 text-gray-600">{stock.name}</td>
                          <td className="py-3 px-4 text-right font-medium">${stock.price.toFixed(2)}</td>
                          <td className={`py-3 px-4 text-right ${stock.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            <div className="flex items-center justify-end gap-1">
                              {stock.change >= 0 ? 
                                <ArrowUpRight className="h-3 w-3" /> : 
                                <ArrowDownRight className="h-3 w-3" />
                              }
                              {stock.change.toFixed(2)} ({stock.changePercent.toFixed(2)}%)
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge variant="secondary">{stock.sector}</Badge>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center py-4 text-gray-500">
                          No stocks found matching your search criteria
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Stock Detail and Trading Panel */}
            <div>
              {selectedStock ? (
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{selectedStock.symbol}</CardTitle>
                        <CardDescription>{selectedStock.name}</CardDescription>
                      </div>
                      <div className={`text-right ${selectedStock.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        <div className="text-lg font-bold">${selectedStock.price.toFixed(2)}</div>
                        <div className="flex items-center justify-end text-sm">
                          {selectedStock.change >= 0 ? 
                            <ArrowUpRight className="h-3 w-3 mr-1" /> : 
                            <ArrowDownRight className="h-3 w-3 mr-1" />
                          }
                          {selectedStock.change.toFixed(2)} ({selectedStock.changePercent.toFixed(2)}%)
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Price Chart */}
                    <div className="h-[200px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsLineChart data={stockHistoricalData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                          <XAxis 
                            dataKey="date" 
                            stroke="#888888" 
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return `${date.getMonth() + 1}/${date.getDate()}`;
                            }}
                          />
                          <YAxis stroke="#888888" domain={['auto', 'auto']} />
                          <RechartsTooltip />
                          <Line
                            type="monotone"
                            dataKey="price"
                            stroke={selectedStock.change >= 0 ? "#10B981" : "#EF4444"}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 8 }}
                          />
                        </RechartsLineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Stock Info */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Market Cap:</span> {selectedStock.marketCap}
                      </div>
                      <div>
                        <span className="text-gray-500">Volume:</span> {selectedStock.volume.toLocaleString()}
                      </div>
                      <div>
                        <span className="text-gray-500">P/E Ratio:</span> {selectedStock.peRatio.toFixed(2)}
                      </div>
                      <div>
                        <span className="text-gray-500">Dividend Yield:</span> {selectedStock.dividendYield.toFixed(2)}%
                      </div>
                    </div>

                    <div className="text-sm">
                      <p className="text-gray-700">{selectedStock.description}</p>
                    </div>

                    {/* Trading Interface */}
                    <div className="space-y-3 pt-2">
                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium w-20">Position:</div>
                        <div>
                          {portfolio[selectedStock.symbol] ? (
                            <div>
                              <span className="font-medium">{portfolio[selectedStock.symbol].shares}</span> shares @ <span className="font-medium">${portfolio[selectedStock.symbol].avgPrice.toFixed(2)}</span>
                            </div>
                          ) : (
                            <div className="text-gray-500">No position</div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium w-20">Value:</div>
                        <div>
                          {portfolio[selectedStock.symbol] ? (
                            <div className="font-medium">${(portfolio[selectedStock.symbol].shares * selectedStock.price).toFixed(2)}</div>
                          ) : (
                            <div className="text-gray-500">$0.00</div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium w-20">Quantity:</div>
                        <Input
                          type="number"
                          min="1"
                          className="max-w-24 h-8"
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        />
                      </div>

                      <div className="flex items-center space-x-2 pt-2">
                        <Button 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={handleBuyStock}
                        >
                          Buy
                        </Button>
                        <Button 
                          className="flex-1 bg-red-600 hover:bg-red-700"
                          onClick={handleSellStock}
                          disabled={!portfolio[selectedStock.symbol] || portfolio[selectedStock.symbol].shares < quantity}
                        >
                          Sell
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-full flex items-center justify-center p-6">
                  <div className="text-center text-gray-500">
                    <Info className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium mb-2">Select a Stock</h3>
                    <p className="text-sm">Click on any stock in the list to view details and trade</p>
                  </div>
                </Card>
              )}
            </div>
          </div>

          {/* Portfolio Section */}
          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">Your Portfolio</h3>
            {Object.keys(portfolio).length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-500">Symbol</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-500">Name</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-gray-500">Shares</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-gray-500">Avg. Price</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-gray-500">Current Price</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-gray-500">Market Value</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-gray-500">Gain/Loss</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(portfolio).map(([symbol, position]) => {
                    const stock = mockStocks.find(s => s.symbol === symbol);
                    if (!stock) return null;
                    
                    const marketValue = stock.price * position.shares;
                    const costBasis = position.avgPrice * position.shares;
                    const gainLoss = marketValue - costBasis;
                    const gainLossPercent = (gainLoss / costBasis) * 100;
                    
                    return (
                      <tr 
                        key={symbol} 
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleSelectStock(stock)}
                      >
                        <td className="py-3 px-4 font-medium">{symbol}</td>
                        <td className="py-3 px-4 text-gray-600">{stock.name}</td>
                        <td className="py-3 px-4 text-right">{position.shares}</td>
                        <td className="py-3 px-4 text-right">${position.avgPrice.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right">${stock.price.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right font-medium">${marketValue.toFixed(2)}</td>
                        <td className={`py-3 px-4 text-right ${gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${gainLoss.toFixed(2)} ({gainLossPercent.toFixed(2)}%)
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                <p>You don't have any positions yet. Start investing by selecting a stock and clicking "Buy".</p>
              </div>
            )}
          </div>

          {/* Sector Performance */}
          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">Sector Performance</h3>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={sectorPerformanceData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar 
                    dataKey="value" 
                    radius={[4, 4, 0, 0]}
                    label={{ position: 'top', fill: '#666', fontSize: 12 }}
                  >
                    {sectorPerformanceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
